import { useEffect, useRef, type RefObject } from "react";

export type LayerDismissReason = "escape" | "outside";

export type PlatformLayerOptions = {
  containFirstElementOnly?: boolean;
  additionalInsideRefs?: ReadonlyArray<RefObject<HTMLElement | null>>;
  dismissOnEscape?: boolean;
  dismissOnOutside?: boolean;
  restoreFocus?: boolean;
};

type RegisteredLayer = {
  id: symbol;
  root: () => HTMLElement | null;
  contains: (target: Node) => boolean;
  dismiss: (reason: LayerDismissReason) => void;
  dismissOnEscape: boolean;
  dismissOnOutside: boolean;
  setStackOrder: (order: number) => void;
};

const layerStack: RegisteredLayer[] = [];
let listenersInstalled = false;

function topLayer(): RegisteredLayer | undefined {
  return layerStack[layerStack.length - 1];
}

function synchronizeLayerStackOrder() {
  layerStack.forEach((layer, index) => layer.setStackOrder(index));
}

export function isTopmostPlatformLayer(root: HTMLElement | null): boolean {
  if (!root) return false;
  return topLayer()?.root() === root;
}

export function topmostPlatformLayerRoot(): HTMLElement | null {
  return topLayer()?.root() ?? null;
}

function handleDocumentMouseDown(event: MouseEvent) {
  const layer = topLayer();
  if (!layer || !(event.target instanceof Node) || layer.contains(event.target)) return;
  if (layer.dismissOnOutside) layer.dismiss("outside");
}

function handleDocumentKeyDown(event: KeyboardEvent) {
  if (event.key !== "Escape") return;
  const layer = topLayer();
  if (!layer) return;

  // Escape belongs to the topmost layer even when that layer is intentionally
  // non-dismissible. It must never fall through and close a parent layer.
  event.preventDefault();
  event.stopPropagation();
  if (layer.dismissOnEscape) layer.dismiss("escape");
}

function installListeners() {
  if (listenersInstalled || typeof document === "undefined") return;
  document.addEventListener("mousedown", handleDocumentMouseDown, true);
  document.addEventListener("keydown", handleDocumentKeyDown, true);
  listenersInstalled = true;
}

function removeListenersWhenIdle() {
  if (!listenersInstalled || layerStack.length > 0 || typeof document === "undefined") return;
  document.removeEventListener("mousedown", handleDocumentMouseDown, true);
  document.removeEventListener("keydown", handleDocumentKeyDown, true);
  listenersInstalled = false;
}

function restoreFocus(trigger: HTMLElement | null) {
  if (!trigger) return;
  const restore = () => {
    if (trigger.isConnected) trigger.focus({ preventScroll: true });
  };
  if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(restore);
  } else {
    restore();
  }
}

/**
 * Registers a platform layer in the one shared topmost-layer stack.
 *
 * This is the configurable foundation for menus, popovers, dialogs and
 * drawers. Dismissal only targets the topmost layer. Focus is restored to the
 * opener whenever an open layer closes by Escape, outside click, explicit
 * controls, or state changes, provided the opener still exists. The same
 * registration order also drives visual stacking, so interaction ownership and
 * paint order cannot disagree when layer types are nested.
 */
export function usePlatformLayer<T extends HTMLElement>(
  open: boolean,
  onDismiss: (reason: LayerDismissReason) => void,
  options: PlatformLayerOptions = {},
): RefObject<T | null> {
  const layerRef = useRef<T>(null);
  const dismissRef = useRef(onDismiss);
  const optionsRef = useRef(options);
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);

  dismissRef.current = onDismiss;
  optionsRef.current = options;

  if (open && !wasOpenRef.current && typeof document !== "undefined") {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }
  wasOpenRef.current = open;

  useEffect(() => {
    if (!open) return;

    const id = Symbol("kavoosh-platform-layer");
    const layer: RegisteredLayer = {
      id,
      root: () => layerRef.current,
      get dismissOnEscape() { return optionsRef.current.dismissOnEscape ?? true; },
      get dismissOnOutside() { return optionsRef.current.dismissOnOutside ?? true; },
      dismiss: (reason) => dismissRef.current(reason),
      setStackOrder: (order) => {
        layerRef.current?.style.setProperty("--kavoosh-layer-order", String(order));
      },
      contains: (target) => {
        const root = layerRef.current;
        if (!root) return false;
        const containFirstElementOnly = optionsRef.current.containFirstElementOnly ?? false;
        const semanticDialogRoot = root.getAttribute("role") === "dialog";
        const insideRoot = containFirstElementOnly
          ? semanticDialogRoot
            // Some legacy custom layers put role=dialog on the registered root
            // itself. In that shape, a pointer on the root is the backdrop,
            // while every descendant is legitimate dialog content.
            ? target !== root && root.contains(target)
            : (root.firstElementChild?.contains(target) ?? false)
          : root.contains(target);
        if (insideRoot) return true;
        return (optionsRef.current.additionalInsideRefs ?? [])
          .some((ref) => ref.current?.contains(target) ?? false);
      },
    };

    layerStack.push(layer);
    synchronizeLayerStackOrder();
    installListeners();

    return () => {
      const index = layerStack.findIndex((candidate) => candidate.id === id);
      if (index >= 0) layerStack.splice(index, 1);
      synchronizeLayerStackOrder();
      removeListenersWhenIdle();
      if (optionsRef.current.restoreFocus ?? true) restoreFocus(openerRef.current);
    };
  }, [open]);

  return layerRef;
}

/**
 * Backwards-compatible layer hook used by current Admin and Client surfaces.
 * Existing call sites keep their signature while gaining deterministic focus
 * restoration for every close path.
 */
export function useDismissibleLayer<T extends HTMLElement>(
  open: boolean,
  onDismiss: () => void,
  containFirstElementOnly = false,
  additionalInsideRefs: ReadonlyArray<RefObject<HTMLElement | null>> = [],
): RefObject<T | null> {
  return usePlatformLayer<T>(open, () => onDismiss(), {
    containFirstElementOnly,
    additionalInsideRefs,
    dismissOnEscape: true,
    dismissOnOutside: true,
    restoreFocus: true,
  });
}
